<template>
  <g
    class="rectangle-annotation"
    :class="{ selected, editing }"
  >
    <!-- Rectangle principal -->
    <rect
      :x="Math.min(annotation.data.x1, annotation.data.x2)"
      :y="Math.min(annotation.data.y1, annotation.data.y2)"
      :width="Math.abs(annotation.data.x2 - annotation.data.x1)"
      :height="Math.abs(annotation.data.y2 - annotation.data.y1)"
      :stroke="selected ? '#4a9eff' : (annotation.color || '#ffa500')"
      :stroke-width="selected ? (strokeWidth + 1) : strokeWidth"
      fill="none"
      stroke-linejoin="round"
      class="rect-main"
      :class="{ draggable: selected }"
      @click.stop="handleClick"
      @mousedown.stop="selected ? handleStartRectDrag($event) : null"
    />

    <!-- Points de contrôle (visibles si sélectionné) -->
    <g v-if="selected">
      <!-- Coin supérieur gauche -->
      <circle
        :cx="annotation.data.x1"
        :cy="annotation.data.y1"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'topLeft')"
      />
      <!-- Coin supérieur droit -->
      <circle
        :cx="annotation.data.x2"
        :cy="annotation.data.y1"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'topRight')"
      />
      <!-- Coin inférieur gauche -->
      <circle
        :cx="annotation.data.x1"
        :cy="annotation.data.y2"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'bottomLeft')"
      />
      <!-- Coin inférieur droit -->
      <circle
        :cx="annotation.data.x2"
        :cy="annotation.data.y2"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'bottomRight')"
      />
    </g>
  </g>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Annotation } from '@api/annotations.api';
import {
  ANNOTATION_DEFAULT_STROKE_PX,
  ANNOTATION_HANDLE_RADIUS_PX,
  ANNOTATION_HANDLE_STROKE_PX,
} from '@/constants/annotations';

const props = defineProps<{
  annotation: Annotation;
  selected: boolean;
  svgRef?: SVGSVGElement | null;
  canvasPixelsPerUser?: number;
}>();

const emit = defineEmits<{
  select: [annotation: Annotation];
  update: [annotation: Annotation, data: any, save?: boolean];
  delete: [annotation: Annotation];
}>();

const editing = ref(false);
const dragging = ref(false);
const dragPoint = ref<'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'rect' | null>(null);
const dragStartPos = ref({ x: 0, y: 0 });
const dragStartRect = ref({ x1: 0, y1: 0, x2: 0, y2: 0 });
const lastDraggedData = ref<any | null>(null);

const pixelsPerUser = computed(() => Math.max(props.canvasPixelsPerUser ?? 1, 0.01));
const strokeWidth = computed(() => props.annotation.stroke_width ?? ANNOTATION_DEFAULT_STROKE_PX);
const handleRadius = computed(() => ANNOTATION_HANDLE_RADIUS_PX / pixelsPerUser.value);
const handleStroke = computed(() => ANNOTATION_HANDLE_STROKE_PX / pixelsPerUser.value);

const handleClick = () => {
  if (!props.selected) {
    emit('select', props.annotation);
  }
};

const handleStartDrag = (event: MouseEvent, point: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = point;
  lastDraggedData.value = null;
  event.preventDefault();
  event.stopPropagation();
};

const handleStartRectDrag = (event: MouseEvent) => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = 'rect';
  lastDraggedData.value = null;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  dragStartPos.value = { x: svgP.x, y: svgP.y };
  dragStartRect.value = {
    x1: props.annotation.data.x1,
    y1: props.annotation.data.y1,
    x2: props.annotation.data.x2,
    y2: props.annotation.data.y2,
  };
  
  event.preventDefault();
  event.stopPropagation();
};

const handleDragMove = (event: MouseEvent) => {
  if (!dragging.value || !dragPoint.value || !props.svgRef) return;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  const newData = { ...props.annotation.data };
  
  if (dragPoint.value === 'rect') {
    // Déplacer tout le rectangle
    const deltaX = svgP.x - dragStartPos.value.x;
    const deltaY = svgP.y - dragStartPos.value.y;
    
    newData.x1 = dragStartRect.value.x1 + deltaX;
    newData.y1 = dragStartRect.value.y1 + deltaY;
    newData.x2 = dragStartRect.value.x2 + deltaX;
    newData.y2 = dragStartRect.value.y2 + deltaY;
  } else {
    // Déplacer un coin spécifique
    if (dragPoint.value === 'topLeft') {
      newData.x1 = svgP.x;
      newData.y1 = svgP.y;
    } else if (dragPoint.value === 'topRight') {
      newData.x2 = svgP.x;
      newData.y1 = svgP.y;
    } else if (dragPoint.value === 'bottomLeft') {
      newData.x1 = svgP.x;
      newData.y2 = svgP.y;
    } else if (dragPoint.value === 'bottomRight') {
      newData.x2 = svgP.x;
      newData.y2 = svgP.y;
    }
    
    // Maintenir un carré si Shift est pressé
    if (event.shiftKey) {
      const width = Math.abs(newData.x2 - newData.x1);
      const height = Math.abs(newData.y2 - newData.y1);
      const size = Math.max(width, height);
      
      if (dragPoint.value === 'topLeft') {
        newData.x1 = newData.x2 - size * Math.sign(newData.x2 - newData.x1);
        newData.y1 = newData.y2 - size * Math.sign(newData.y2 - newData.y1);
      } else if (dragPoint.value === 'topRight') {
        newData.x2 = newData.x1 + size * Math.sign(newData.x2 - newData.x1);
        newData.y1 = newData.y2 - size * Math.sign(newData.y2 - newData.y1);
      } else if (dragPoint.value === 'bottomLeft') {
        newData.x1 = newData.x2 - size * Math.sign(newData.x2 - newData.x1);
        newData.y2 = newData.y1 + size * Math.sign(newData.y2 - newData.y1);
      } else if (dragPoint.value === 'bottomRight') {
        newData.x2 = newData.x1 + size * Math.sign(newData.x2 - newData.x1);
        newData.y2 = newData.y1 + size * Math.sign(newData.y2 - newData.y1);
      }
    }
  }
  
  lastDraggedData.value = newData;
  emit('update', props.annotation, newData, false);
};

const handleDragEnd = () => {
  if (dragging.value) {
    const finalData = lastDraggedData.value;
    dragging.value = false;
    dragPoint.value = null;
    lastDraggedData.value = null;
    if (finalData) {
      emit('update', props.annotation, finalData, true);
    }
  }
};

onMounted(() => {
  window.addEventListener('mousemove', handleDragMove);
  window.addEventListener('mouseup', handleDragEnd);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleDragMove);
  window.removeEventListener('mouseup', handleDragEnd);
});
</script>

<style scoped>
.rectangle-annotation {
  cursor: pointer;
}

.rect-main {
  transition: stroke 0.2s, stroke-width 0.2s;
}

.rect-main:hover {
  opacity: 0.92;
}

.rect-main.draggable {
  cursor: move;
}

.control-point {
  cursor: nwse-resize;
}
</style>
